import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import api from '../../services/api';
import { Button } from '../../components/Button';
import { Label } from '../../components/Label';
import { Input } from '../../components/Input';
import { OrdemCard } from '../../components/OrdemCard';
import { OrdemDetalhes } from '../../components/OrdemDetalhes';
import { LogOut, ArrowLeft, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../../utils/formatters';
import { Ordem } from '../../types';
import './styles.css';
import Logo from '../../assets/logo.svg';

export const OrdensFinalizadas: React.FC = () => {
  const { handleClearToken } = useAuth();
  const navigate = useNavigate();
  const [ordens, setOrdens] = useState<Ordem[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [totalSum, setTotalSum] = useState(0);
  const [selectedOrdem, setSelectedOrdem] = useState<Ordem | null>(null);

  const fetchOrdens = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/v1/ordens_servico/');
      const ordensBackend = response.data;
      
      const ordensMapeadas = ordensBackend.map((o: any) => ({
        id: o.id,
        data: o.data,
        pedidos: o.pedidos?.map((p: any) => ({
          id: p.id,
          modelo: typeof p.modelo === 'object' ? p.modelo.id : p.modelo,
          quantidade: p.quantidade,
          material: '',
          preco_unidade: p.preco_unidade,
          preco_total_pedido: p.preco_total_pedido,
          status: p.status,
          cor: p.cor ?? '',
          observacao: p.observacao ?? ''
        })),
        valor_total: o.total,
        status: o.status,
        finalizado_em: o.finalizado_em ?? null
      }));

      // Filtra apenas ordens finalizadas
      let ordensFinalizadas = ordensMapeadas.filter((o: any) => 
        o.status === 'finalizado'
      );

      // Aplica filtro de datas (intervalo fechado, ignorando horário)
      if (dataInicio && dataFim) {
        const inicioStr = dataInicio; // formato YYYY-MM-DD vindo do input date
        const fimStr = dataFim;       // formato YYYY-MM-DD vindo do input date

        ordensFinalizadas = ordensFinalizadas.filter((o: any) => {
          if (!o.finalizado_em) return false;
          // Extrai apenas a parte da data do ISO (YYYY-MM-DD)
          const finalizadoDateOnly = typeof o.finalizado_em === 'string'
            ? o.finalizado_em.slice(0, 10)
            : new Date(o.finalizado_em).toISOString().slice(0, 10);

          return finalizadoDateOnly >= inicioStr && finalizadoDateOnly <= fimStr;
        });
      }

      setOrdens(ordensFinalizadas);

      // Calcula soma dos totais
      const soma = ordensFinalizadas.reduce((acc: number, o: Ordem) => acc + o.valor_total, 0);
      setTotalSum(soma);
    } catch (error) {
      console.error('Erro ao buscar ordens:', error);
      toast.error('Erro ao carregar ordens finalizadas');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    // Verifica se as datas foram preenchidas
    if (!dataInicio || !dataFim) {
      toast.error('Por favor, selecione as datas de início e fim');
      return;
    }
    fetchOrdens();
  };

  const handleLogout = () => {
    handleClearToken();
    navigate('/');
  };

  const handleBack = () => {
    navigate('/main');
  };

  const handleOrdemClick = (ordem: Ordem) => {
    setSelectedOrdem(ordem);
  };

  const handleCloseDetalhes = () => {
    setSelectedOrdem(null);
  };

  return (
    <div className="ordens-finalizadas-page">
      <header className="ordens-finalizadas-header">
        <div className="ordens-finalizadas-header-container">
          <div className="ordens-finalizadas-header-content">
            <img src={Logo} alt="Logo" className="ordens-finalizadas-logo" />
          </div>
          <div className="ordens-finalizadas-header-actions">
            <Button
              variant="outline"
              onClick={handleBack}
              className="ordens-finalizadas-back-button"
            >
              <ArrowLeft className="icon-small" />
              Voltar
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="ordens-finalizadas-logout-button"
            >
              <LogOut className="icon-small" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="ordens-finalizadas-main">
        <div className="ordens-finalizadas-main-header">
          <div className="ordens-finalizadas-main-header-content">
            <h2 className="ordens-finalizadas-section-title">
              Ordens de Serviço Finalizadas
            </h2>
            <p className="ordens-finalizadas-section-subtitle">
              {ordens.length} {ordens.length === 1 ? 'ordem finalizada' : 'ordens finalizadas'}
            </p>
          </div>
        </div>

        <div className="ordens-finalizadas-filters">
          <div className="ordens-finalizadas-filter-group">
            <Label htmlFor="data-inicio">Data Início</Label>
            <Input
              id="data-inicio"
              type="date"
              value={dataInicio}
              onChange={e => setDataInicio(e.target.value)}
            />
          </div>
          <div className="ordens-finalizadas-filter-group">
            <Label htmlFor="data-fim">Data Fim</Label>
            <Input
              id="data-fim"
              type="date"
              value={dataFim}
              onChange={e => setDataFim(e.target.value)}
            />
          </div>
          <Button
            onClick={handleFilter}
            className="ordens-finalizadas-filter-button"
          >
            <Filter className="icon-small" />
            Filtrar
          </Button>
        </div>

        {loading ? (
          <div className="ordens-finalizadas-loading">
            <div className="ordens-finalizadas-spinner"></div>
          </div>
        ) : ordens.length === 0 ? (
          <div className="ordens-finalizadas-empty">
            <p className="ordens-finalizadas-empty-text">
              {dataInicio && dataFim 
                ? 'Nenhuma ordem finalizada no período selecionado.' 
                : 'Nenhuma ordem de serviço finalizada.'}
            </p>
          </div>
        ) : (
          <div className="ordens-finalizadas-content">
            <div className="ordens-finalizadas-total">
              <h3 className="ordens-finalizadas-total-title">Total Geral</h3>
              <p className="ordens-finalizadas-total-value">
                {formatCurrency(totalSum)}
              </p>
            </div>

            <div className="dashboard-cards-fullwidth">
              {ordens.map((ordem) => (
                <OrdemCard
                  key={ordem.id}
                  ordem={ordem}
                  onClick={() => handleOrdemClick(ordem)}
                />
              ))}
            </div>
          </div>
        )}
      </main>

      {selectedOrdem && (
        <OrdemDetalhes
          ordem={selectedOrdem}
          onClose={handleCloseDetalhes}
        />
      )}
    </div>
  );
};

